import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Play, Loader2, AlertCircle } from 'lucide-react';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const formatApiError = (detail) => {
    if (detail == null) return 'Something went wrong. Please try again.';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(' ');
    }
    if (detail?.msg) return detail.msg;
    return String(detail);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center relative"
      style={{
        backgroundImage: `linear-gradient(to top, #0A0A0A 0%, rgba(10,10,10,0.6) 50%, #0A0A0A 100%), url('https://images.unsplash.com/photo-1761502479994-3a5e07ec243e?w=1920&q=80')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="w-full max-w-md mx-4">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-14 h-14 bg-[#E50914] rounded-xl flex items-center justify-center animate-pulse-glow">
            <Play className="w-7 h-7 text-white fill-current" />
          </div>
          <span className="text-3xl font-black tracking-tighter font-['Outfit']">StreamHub</span>
        </div>

        {/* Login Card */}
        <div className="bg-[#121212]/90 backdrop-blur-xl border border-[#262626] rounded-2xl p-8" data-testid="login-form">
          <h1 className="text-2xl font-bold text-center mb-2 font-['Outfit']">Welcome Back</h1>
          <p className="text-[#A3A3A3] text-center mb-8">Sign in to continue streaming</p>

          {error && (
            <div className="flex items-center gap-3 p-4 bg-[#E50914]/10 border border-[#E50914]/30 rounded-lg mb-6" data-testid="login-error">
              <AlertCircle className="w-5 h-5 text-[#E50914] flex-shrink-0" />
              <p className="text-[#E50914] text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-xs tracking-[0.2em] uppercase text-[#A3A3A3] font-semibold mb-2">
                Email
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field w-full px-4 py-3 text-white"
                placeholder="Enter your email"
                required
                data-testid="email-input"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-xs tracking-[0.2em] uppercase text-[#A3A3A3] font-semibold mb-2">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-field w-full px-4 py-3 text-white"
                placeholder="Enter your password"
                required
                data-testid="password-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full py-3 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="login-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <p className="text-[#A3A3A3] text-sm text-center mt-6">
            Contact your administrator for access
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
